import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
// Registrar los componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ChartDataLabels
)

const formatNumberWithCommas = (number) => {
  if (typeof number !== 'number') return number
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const KpiCustomGraphicBar = ({
  barDataStatus = {
    labels: ['Dato1', 'Dato2'],
    datasets: [
      // Cada dataset es una sección de la barra
      {
        label: 'Generados',
        data: [10.45, 26.34],
        backgroundColor: '#FF0000',
        borderColor: '#388e3c',
        borderWidth: 1,
        // stack: 'Stack 0', // Opcional, si quieres varios grupos de apilado
      },
      {
        label: 'Resueltos',
        data: [12.45, 23.34],
        backgroundColor: '#0000FF',
        borderColor: '#1E40AF',
        borderWidth: 1,
        // stack: 'Stack 0',
      },
    ],
  },
  barOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          padding: 10,
        },
      },
      datalabels: {
        anchor: window.innerWidth <= 768 ? 'end' : 'center',
        align: window.innerWidth <= 768 ? 'end' : 'center',
        offset: 10,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
      x: {
        ticks: {
          color: '#4A4A4A',
          font: {
            size: 12,
            weight: 'bold',
          },
        },
      },
    },
  },
  fromPdf = false,
  legendCustom = false,
  showStackedDatalabels = false, //Show totals on top of each stack
}) => {
  const [isMobile, setIsMobile] = useState(false)
  const chartRef = useRef(null)

  useEffect(() => {
    const handleResize = () => {
      if (!fromPdf) {
        setIsMobile(window.innerWidth <= 768)
        // Forzar resize del gráfico en cada cambio de tamaño
        if (chartRef.current) {
          if (typeof chartRef.current.resize === 'function') {
            chartRef.current.resize()
          } else if (
            chartRef.current.chartInstance &&
            typeof chartRef.current.chartInstance.resize === 'function'
          ) {
            chartRef.current.chartInstance.resize()
          }
        }
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const getStackTotals = (barDataStatus) => {
    // Calcula el total por stack y por índice
    if (!barDataStatus?.datasets?.length) return {}
    const stackTotals = {}
    barDataStatus.datasets.forEach((ds) => {
      const stack = ds.stack || 'default'
      if (!stackTotals[stack])
        stackTotals[stack] = Array(barDataStatus.labels.length).fill(0)
      ds.data.forEach((val, idx) => {
        stackTotals[stack][idx] += val || 0
      })
    })
    return stackTotals
  }

  const stackTotals = getStackTotals(barDataStatus)

  // Devuelve el índice del dataset superior de cada stack para cada barra
  const getTopDatasetIndexes = (barDataStatus) => {
    const topIndexes = {}
    barDataStatus.datasets.forEach((ds, dsIdx) => {
      const stack = ds.stack || 'default'
      if (!topIndexes[stack]) topIndexes[stack] = []
      ds.data.forEach((_, idx) => {
        topIndexes[stack][idx] = dsIdx
      })
    })
    return topIndexes
  }

  const topDatasetIndexes = getTopDatasetIndexes(barDataStatus)

  // Modifica datalabels para mostrar el total en la parte superior de cada stack
  const getStackedDatalabels = (isMobile) => ({
    display: function (context) {
      const { dataset, datasetIndex, dataIndex, chart } = context
      const stack = dataset.stack || 'default'
      // Mostrar solo en el dataset superior de cada stack
      return (
        topDatasetIndexes[stack] &&
        topDatasetIndexes[stack][dataIndex] === datasetIndex
      )
    },
    formatter: function (value, context) {
      const { dataset, dataIndex } = context
      const stack = dataset.stack || 'default'
      if (
        stackTotals[stack] &&
        typeof stackTotals[stack][dataIndex] !== 'undefined'
      ) {
        return formatNumberWithCommas(stackTotals[stack][dataIndex])
      }
      return ''
    },
    anchor: fromPdf ? barOptions?.plugins?.datalabels?.anchor : isMobile ? 'end' : barOptions?.plugins?.datalabels?.anchor,
    align: fromPdf ? barOptions?.plugins?.datalabels?.align : isMobile ? 'end' : barOptions?.plugins?.datalabels?.align,
    offset: fromPdf ? barOptions?.plugins?.datalabels?.offset : isMobile ? -10 : barOptions?.plugins?.datalabels?.offset,
    backgroundColor: fromPdf ? barOptions?.plugins?.datalabels?.backgroundColor : isMobile ? 'rgba(0, 0, 0, 0.7)' : barOptions?.plugins?.datalabels?.backgroundColor,
    borderRadius: fromPdf ? barOptions?.plugins?.datalabels?.borderRadius : isMobile ? 4 : barOptions?.plugins?.datalabels?.borderRadius,
    color: fromPdf ? barOptions?.plugins?.datalabels?.color : isMobile ? '#FFFFFF' : barOptions?.plugins?.datalabels?.color,
    padding: fromPdf ? barOptions?.plugins?.datalabels?.padding : isMobile ? 4 : barOptions?.plugins?.datalabels?.padding,
    font: {
      size: fromPdf ? barOptions?.plugins?.datalabels?.font?.size : isMobile ? 10 : barOptions?.plugins?.datalabels?.font?.size,
      weight: fromPdf ? barOptions?.plugins?.datalabels?.font?.weight : barOptions?.plugins?.datalabels?.font?.weight,
    },
  })

  let barOptionsMobile = {
    ...barOptions,
    plugins: {
      ...barOptions?.plugins,
      legend: {
        ...barOptions?.plugins?.legend,
        position: 'bottom',
      },
      datalabels: showStackedDatalabels
        ? getStackedDatalabels(true)
        : barOptions?.plugins?.datalabels, // Si es false, usa el datalabels original
    },
    scales: {
      ...barOptions.scales,
      y: {
        ...barOptions.scales?.y,
        stacked: true, // <-- Asegura apilado en móvil
      },
      x: {
        ...barOptions.scales?.x,
        stacked: true, // <-- Asegura apilado en móvil
      },
    },
  }
  
  return (
    <div className="px-1 kpi-graphics">
      <div className="row kpi-card-container kpi-card-containerMini">
        {barDataStatus && barOptions && (
          <>
            {/* <Bar
              data={barDataStatus}
              options={{
                ...barOptions,
                plugins: {
                  ...barOptions.plugins,
                  datalabels: {
                    formatter: (value) => formatNumberWithCommas(value),
                    anchor: 'end',
                    align: 'end',
                    color: '#000',
                    font: { weight: 'bold' },
                    offset: -3,
                  },
                },
              }}
            /> */}
            {legendCustom && (
              <div
                style={{
                  marginBottom: 5,
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 10,
                }}
              >
                {barDataStatus.datasets.map((ds, idx) => (
                  <div
                    key={idx}
                    style={{ display: 'flex', alignItems: 'center', gap: 2 }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: 20,
                        height: 12,
                        background: ds.backgroundColor,
                        borderRadius: 2,
                        marginRight: 1,
                      }}
                    />
                    <span
                      style={{
                        color: '#4A4A4A',
                        fontWeight: 'bold',
                        fontSize: 12,
                      }}
                    >
                      {ds.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <Bar
              ref={chartRef} // <-- Añadido
              data={barDataStatus}
              options={
                isMobile
                  ? barOptionsMobile
                  : {
                      ...barOptions,
                      plugins: {
                        ...barOptions.plugins,
                        datalabels: showStackedDatalabels
                          ? getStackedDatalabels(false)
                          : barOptions?.plugins?.datalabels, // Si es false, usa el datalabels original
                      },
                      scales: {
                        ...barOptions.scales,
                        y: {
                          ...barOptions.scales?.y,
                          stacked: true,
                        },
                        x: {
                          ...barOptions.scales?.x,
                          stacked: true,
                        },
                      },
                    }
              }
            />
          </>
        )}
      </div>
    </div>
  )
}

KpiCustomGraphicBar.propTypes = {
  barDataStatus: PropTypes.object,
  barOptions: PropTypes.object,
  showStackedDatalabels: PropTypes.bool, // Añade la propType
}

export default KpiCustomGraphicBar
